import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, User, Menu, ChevronDown, X } from "lucide-react";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import { LANGUAGES, categories } from "../mock";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "./ui/sheet";

const Header = () => {
  const { t, lang, setLang, cartCount } = useApp();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);

  const activeLang = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  const submitSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/c/all?q=${encodeURIComponent(query.trim())}`);
      setMobileOpen(false);
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-[#faf8f5]/95 backdrop-blur border-b border-border">
      {/* Promo bar */}
      <div className="bg-[#1b1b1b] text-[#faf8f5] text-[11px] md:text-xs tracking-[0.15em] uppercase text-center py-2 px-4">
        {t("promoBar")}
      </div>

      {/* Main bar */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-[68px] gap-4">
          {/* Mobile menu */}
          <div className="flex items-center gap-2 md:hidden">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger aria-label="menu">
                <Menu className="h-6 w-6" strokeWidth={1.5} />
              </SheetTrigger>
              <SheetContent side="left" className="w-[300px] bg-[#faf8f5]">
                <div className="mt-8 flex flex-col gap-1">
                  <form onSubmit={submitSearch} className="relative mb-6">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                    <input
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder={t("searchPlaceholder")}
                      className="w-full pl-10 pr-3 py-2.5 bg-white border border-border rounded-sm text-sm outline-none focus:border-[#b08d57]"
                    />
                  </form>
                  {categories.map((c) => (
                    <Link
                      key={c.id}
                      to={`/c/${c.id}`}
                      onClick={() => setMobileOpen(false)}
                      className="py-3 border-b border-border text-[15px] tracking-wide"
                    >
                      {t(c.key)}
                    </Link>
                  ))}
                  <Link to="/c/all?sale=1" onClick={() => setMobileOpen(false)} className="py-3 border-b border-border text-[15px] text-[#8b1e3f] tracking-wide">
                    {t("sale")}
                  </Link>
                </div>
              </SheetContent>
            </Sheet>
          </div>

          {/* Logo */}
          <Link to="/" className="flex-shrink-0 md:flex-1 md:flex md:justify-start">
            <div className="leading-none">
              <span className="font-serif-lux text-2xl md:text-[28px] font-semibold tracking-tight text-[#1b1b1b]">
                Amour<span className="text-[#b08d57]">duluxe</span>
              </span>
            </div>
          </Link>

          {/* Search - desktop */}
          <form onSubmit={submitSearch} className="hidden md:block flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("searchPlaceholder")}
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-border rounded-sm text-sm outline-none focus:border-[#b08d57] transition-colors"
              />
            </div>
          </form>

          {/* Actions */}
          <div className="flex items-center gap-3 md:gap-5 md:flex-1 md:justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-sm tracking-wide outline-none">
                {activeLang.flag}
                <ChevronDown className="h-3.5 w-3.5" strokeWidth={1.5} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white">
                {LANGUAGES.map((l) => (
                  <DropdownMenuItem
                    key={l.code}
                    onClick={() => setLang(l.code)}
                    className={`cursor-pointer text-sm ${l.code === lang ? "text-[#b08d57] font-medium" : ""}`}
                  >
                    {l.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger className="hidden md:flex items-center gap-1.5 text-sm tracking-wide hover:text-[#b08d57] transition-colors outline-none">
                  <User className="h-5 w-5" strokeWidth={1.5} />
                  <span className="hidden lg:inline max-w-[90px] truncate">{user.name}</span>
                  <ChevronDown className="h-3.5 w-3.5" strokeWidth={1.5} />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white">
                  <DropdownMenuItem onClick={() => navigate("/account")} className="cursor-pointer text-sm">{t("myAccount")}</DropdownMenuItem>
                  {user.role === "seller" && (
                    <DropdownMenuItem onClick={() => navigate("/seller")} className="cursor-pointer text-sm">{t("sellerSpace")}</DropdownMenuItem>
                  )}
                  {user.role === "admin" && (
                    <DropdownMenuItem onClick={() => navigate("/admin")} className="cursor-pointer text-sm">{t("adminSpace")}</DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} className="cursor-pointer text-sm text-[#8b1e3f]">{t("logout")}</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/auth" className="hidden md:flex items-center gap-1.5 text-sm tracking-wide hover:text-[#b08d57] transition-colors">
                <User className="h-5 w-5" strokeWidth={1.5} />
                <span className="hidden lg:inline">{t("login")}</span>
              </Link>
            )}

            <Link to="/cart" className="relative flex items-center hover:text-[#b08d57] transition-colors" aria-label="cart">
              <ShoppingBag className="h-5 w-5" strokeWidth={1.5} />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#b08d57] text-white text-[10px] font-medium h-4 min-w-4 px-1 rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>
          </div>
        </div>
      </div>

      {/* Category nav - desktop */}
      <nav className="hidden md:block border-t border-border">
        <div className="max-w-[1400px] mx-auto px-8">
          <ul className="flex items-center justify-center gap-10 h-11 text-[13px] tracking-[0.12em] uppercase">
            {categories.map((c) => (
              <li key={c.id}>
                <Link to={`/c/${c.id}`} className="nav-underline text-[#333] hover:text-[#1b1b1b]">
                  {t(c.key)}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/vip" className="nav-underline text-[#b08d57] font-medium">
                {t("vipNav")}
              </Link>
            </li>
            <li>
              <Link to="/c/all?sale=1" className="nav-underline text-[#8b1e3f] font-medium">
                {t("sale")}
              </Link>
            </li>
          </ul>
        </div>
      </nav>
    </header>
  );
};

export default Header;
